import { useMutation, useQuery } from '@apollo/client';
import { checkFeatures } from '@bybeam/platform-lib/features/check';
import {
  AccountType,
  BankAccount,
  FeatureName,
  MutationResponse,
  ProgramStatus,
  SaveUserBankAccountInput,
  TransactionErrorDisplay,
  User,
} from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import Dropdown from '@platform-ui-common/components/Dropdown/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Loading from '@platform-ui-common/components/Loading/index.js';
import Message from '@platform-ui-common/components/Message/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartnerPrograms from '@platform-ui-common/hooks/contexts/PartnerPrograms/index.js';
import useForm, {
  ValidationMode,
  defaultRequiredErrorMessage,
  fieldUpdate,
  requiredField,
  resetForm,
} from '@platform-ui-common/hooks/useForm/index.js';
import useSaveIndicator from '@platform-ui-common/hooks/useSaveIndicator.js';
import { mask } from '@platform-ui-common/utilities/format.js';
import { AccountTypesDisplay } from '@platform-ui-common/utilities/payment.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import GetAuthenticatedPayeeDetailsQuery from '@platform-ui/claim/graphql/GetAuthenticatedPayeeDetails.graphql';
import isAccountNumberValid from '@platform-ui/utilities/accountNumber.js';
import isRoutingNumberValid from '@platform-ui/utilities/routingNumber.js';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom-v5-compat';
import SaveBankAccountMutation from './SaveBankAccountMutation.graphql';

function Form({
  id,
  bankAccount,
  redirectTo,
  hideProgramUsageMessage = false,
}: {
  id: string;
  bankAccount?: BankAccount;
  redirectTo?: string;
  hideProgramUsageMessage?: boolean;
}): JSX.Element {
  const { programs } = usePartnerPrograms();
  const programsInUse = programs?.filter(
    (program) =>
      program.status !== ProgramStatus.Closed &&
      checkFeatures(program.features, [
        FeatureName.PaymentsPartnerIssued,
        FeatureName.PaymentsApplicants,
        FeatureName.PaymentsDirectDeposit,
      ]),
  );

  const [mode, setMode] = useState<'VIEW' | 'EDIT'>(bankAccount ? 'VIEW' : 'EDIT');

  const initialState = {
    accountType: bankAccount?.accountType ?? ('' as AccountType),
    routingNumber: bankAccount?.routingNumber ?? '',
    confirmRoutingNumber: bankAccount?.routingNumber ?? '',
    accountNumber: bankAccount?.accountNumber ?? '',
    confirmAccountNumber: '',
  };
  const {
    formData,
    trySubmit,
    dispatch,
    errors,
    resetForm: resetSubmit,
    counters: { missingRequiredFields },
  } = useForm(
    initialState,
    {
      accountType: requiredField(undefined, defaultRequiredErrorMessage('Account Type')),
      routingNumber: requiredField(
        ({ routingNumber }) => isRoutingNumberValid(routingNumber),
        defaultRequiredErrorMessage('Routing Number'),
      ),
      confirmRoutingNumber: requiredField(
        ({ routingNumber, confirmRoutingNumber }) =>
          routingNumber !== confirmRoutingNumber && 'Must match routing number',
        defaultRequiredErrorMessage('Confirm Routing Number'),
      ),
      accountNumber: requiredField(
        ({ accountNumber }) => isAccountNumberValid(accountNumber),
        defaultRequiredErrorMessage('Account Number'),
      ),
      confirmAccountNumber: requiredField(
        ({ accountNumber, confirmAccountNumber }) =>
          accountNumber !== confirmAccountNumber && 'Must match account number',
        defaultRequiredErrorMessage('Confirm Account Number'),
      ),
    },
    ValidationMode.OnSubmit,
  );

  const [saveBankAccount, { loading, data, error: transportError }] = useMutation<
    { user: { saveBankAccount: MutationResponse<User> } },
    { input: SaveUserBankAccountInput }
  >(SaveBankAccountMutation);

  const history = useHistory();
  const showSaveIndicator = useSaveIndicator();
  const onSubmit = (): void =>
    trySubmit(async () => {
      const {
        data: {
          user: {
            saveBankAccount: { metadata },
          },
        },
      } = await saveBankAccount({
        variables: {
          input: {
            id,
            bankAccount: {
              accountType: formData.accountType,
              routingNumber: formData.routingNumber,
              accountNumber: formData.accountNumber,
            },
          },
        },
      });

      if (metadata.status >= 400) return;

      showSaveIndicator();
      if (redirectTo) history.push(redirectTo);
      else setMode('VIEW');
    });

  const onEdit = (): void => {
    dispatch(
      resetForm({
        ...initialState,
        accountNumber: '',
        confirmAccountNumber: '',
      }),
    );
    // Otherwise errors show on the account number fields...why...
    setTimeout(() => {
      resetSubmit();
      setMode('EDIT');
    }, 0);
  };

  const location = useLocation();
  const canGoBack = location.key !== 'default';

  const onCancel = (): void => {
    if (bankAccount) {
      setMode('VIEW');
      dispatch(resetForm(initialState));
      resetSubmit();
    } else if (canGoBack) history.goBack();
  };

  const defaultError =
    'We were unable to save your bank account details. Please refresh and try again.';
  let error: string;
  if (transportError) error = defaultError;
  else if (data?.user.saveBankAccount.metadata.errors) {
    const [code, message] = data.user.saveBankAccount.metadata.errors;
    error = TransactionErrorDisplay[code] ?? message ?? defaultError;
  }

  const inputProps = { readOnly: mode === 'VIEW' };

  return (
    <FormContainer
      title="Bank Account Details"
      buttons={
        <div className="flex flex-col gap-4 items-center">
          {mode === 'EDIT' ? (
            <>
              <Button type="submit" loading={loading}>
                Submit
              </Button>
              {(bankAccount || canGoBack) && (
                <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
                  Cancel
                </Button>
              )}
            </>
          ) : (
            <>
              <Button type="button" onClick={onEdit}>
                Edit
              </Button>
              <Button
                type="button"
                variant="outline"
                link={redirectTo ?? ApplicantRoutes.APPLICATION_PORTAL}
                onClick={() => {
                  if (redirectTo && canGoBack) {
                    history.push(redirectTo);
                  }
                }}
              >
                {redirectTo ? 'Save And Continue' : 'Back to Home'}
              </Button>
            </>
          )}
        </div>
      }
      onSubmit={onSubmit}
    >
      <div className="w-full lg:w-7/12 mt-2 mb-4">
        <Typography variant="largeBody" alignment="center">
          Add your preferred bank account details in the following fields.
        </Typography>
      </div>
      <Dropdown
        id="accountType"
        label="Account Type"
        value={formData.accountType}
        items={[
          {
            label: AccountTypesDisplay[AccountType.Checking],
            value: AccountType.Checking,
          },
          {
            label: AccountTypesDisplay[AccountType.Savings],
            value: AccountType.Savings,
          },
        ]}
        onChange={(value: AccountType): void => {
          dispatch(fieldUpdate('accountType', value));
        }}
        required
        error={errors.accountType as string}
        inputProps={inputProps}
      />
      <TextInput
        id="routingNumber"
        label="Routing Number"
        value={formData.routingNumber}
        onChange={(value): void => dispatch(fieldUpdate('routingNumber', value))}
        error={errors.routingNumber as string}
        type="text"
        required
        inputProps={inputProps}
      />
      {mode === 'EDIT' && (
        <TextInput
          id="confirmRoutingNumber"
          label="Confirm Routing Number"
          value={formData.confirmRoutingNumber}
          onChange={(value): void => dispatch(fieldUpdate('confirmRoutingNumber', value))}
          error={errors.confirmRoutingNumber as string}
          type="text"
          required
        />
      )}
      <TextInput
        id="accountNumber"
        label="Account Number"
        value={mode === 'VIEW' ? mask(formData.accountNumber) : formData.accountNumber}
        onChange={(value): void => dispatch(fieldUpdate('accountNumber', value))}
        onPaste={(e: React.ChangeEvent<HTMLInputElement>): void => e.preventDefault()}
        error={errors.accountNumber as string}
        type="text"
        required
        inputProps={inputProps}
      />
      {mode === 'EDIT' && (
        <>
          <TextInput
            id="confirmAccountNumber"
            label="Confirm Account Number"
            value={formData.confirmAccountNumber}
            onChange={(value): void => dispatch(fieldUpdate('confirmAccountNumber', value))}
            onPaste={(e: React.ChangeEvent<HTMLInputElement>): void => e.preventDefault()}
            error={errors.confirmAccountNumber as string}
            type="text"
            required
          />
          <div className="flex flex-col gap-y-4 max-w-field">
            {!hideProgramUsageMessage &&
              programsInUse &&
              (programsInUse.length > 0 ? (
                <Message variant="info">
                  We need your bank account details for the listed programs so that we can pay you
                  directly if you are approved:
                  <ul className="list-disc list-inside">
                    {programsInUse.map((program, idx) => (
                      <li key={program.id}>
                        <strong>{program.name}</strong>
                      </li>
                    ))}
                  </ul>
                </Message>
              ) : (
                <Message variant="info">
                  You don't need to enter your bank account details right now. For other programs
                  that are administering payments, you'll be able to claim by interacting with an
                  approved application.
                </Message>
              ))}
            <Message variant="info">
              Please review and confirm the above information before submitting.
            </Message>
            <ErrorDisplay errors={missingRequiredFields} visible={missingRequiredFields > 0} />
            {error && <Message variant="error">{error}</Message>}
          </div>
        </>
      )}
    </FormContainer>
  );
}

export default function EditBankAccount({
  redirectTo,
  hideProgramUsageMessage,
}: { redirectTo: string; hideProgramUsageMessage?: boolean }): JSX.Element {
  const { data, loading } = useQuery<{ currentUser: User }>(GetAuthenticatedPayeeDetailsQuery, {
    variables: { includeBankAccount: true },
  });
  return loading ? (
    <div className="w-full h-full flex justify-center items-center">
      <Loading size="XL" />
    </div>
  ) : (
    <Form
      {...data.currentUser}
      redirectTo={redirectTo}
      hideProgramUsageMessage={hideProgramUsageMessage}
    />
  );
}
